/**
 * Dashboard Preferences Context
 * Manages user preferences for dashboard customization
 */

import { createContext, useContext, useState, useEffect } from 'react';

const DashboardPreferencesContext = createContext({
  preferences: {},
  setPreference: () => {},
  resetPreferences: () => {},
});

const DEFAULT_PREFERENCES = {
  showCharts: true,
  showTables: true,
  showSecondaryMetrics: true,
  showGuides: true,
  showReports: true,
  showMaintenance: true,
  showPricing: true,
  showHubLinks: true,
};

export function DashboardPreferencesProvider({ children }) {
  const [preferences, setPreferencesState] = useState(DEFAULT_PREFERENCES);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Load from localStorage
    const saved = localStorage.getItem('owner-dashboard-preferences');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferencesState({ ...DEFAULT_PREFERENCES, ...parsed });
      } catch (err) {
        console.error('[DashboardPreferences] Failed to parse saved preferences:', err);
      }
    }
  }, []);

  const setPreference = (key, value) => {
    setPreferencesState((prev) => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem('owner-dashboard-preferences', JSON.stringify(updated));
      return updated;
    });
  };

  const resetPreferences = () => {
    setPreferencesState(DEFAULT_PREFERENCES);
    localStorage.removeItem('owner-dashboard-preferences');
  };

  if (!mounted) {
    return <>{children}</>;
  }

  return (
    <DashboardPreferencesContext.Provider value={{ preferences, setPreference, resetPreferences }}>
      {children}
    </DashboardPreferencesContext.Provider>
  );
}

export function useDashboardPreferences() {
  const context = useContext(DashboardPreferencesContext);
  if (!context) {
    throw new Error('useDashboardPreferences must be used within DashboardPreferencesProvider');
  }
  return context;
}

